import { Injectable } from '@angular/core';
import {Features, ServiceDetails} from "./service-details";
import { User } from "./user";
import { HttpClient } from "@angular/common/http";
import { DrupalPlanDetails } from "../my-plan/drupal-plan-details";
import { BehaviorSubject, Subject } from "rxjs";
import { SiteDetails } from "./site-details";
import { CallPack } from "../manage-call-pack/call-pack";
import { FetchOptions, StbOption } from "./fetch-options";
import { MobileInProgressChangeOfPlanDetails } from "../my-plan/change-plan/mobile-in-progress-change-of-plan-details";
import { LinkedAccount } from "./linked-account";
import { Constants } from "./constants";
import {LinkedAccountBalance, LinkedAccountBalanceResponse} from "./linked-account-balance-response";
import {MobilePlanDetails} from "../dashboard/mobile-plan-details";
import {BillingDetails} from "./billing-details";
import { OrderDetails } from "./order-details";

@Injectable()
export class CommonDataService {
  private _user: User;
  private _userCallPacks: CallPack[];
  private _userFetchOptions: FetchOptions;
  private _newFetchOptions: FetchOptions;
  private _stbOption: StbOption;
  private _mobileInProgressCopDetails: MobileInProgressChangeOfPlanDetails = null
  private _serviceDetailsList: ServiceDetails[];
  private _selectedServiceDetails: BehaviorSubject<ServiceDetails> = new BehaviorSubject<ServiceDetails>(null);
  private _billingAccountDetails : BillingDetails;
  private _brand: BehaviorSubject<string>  = new BehaviorSubject('');
  private _csrMode: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _amountOwing: number;
  private _siteDetails: BehaviorSubject<SiteDetails> = new BehaviorSubject(new SiteDetails());
  private _isBpaInflight: BehaviorSubject<Map<string, boolean>> = new BehaviorSubject<Map<string, boolean>>(null);
  private _linkedAccountBalances: BehaviorSubject<Map<string, number>> = new BehaviorSubject<Map<string, number>>(null);
  private _technology: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private _linkedAccounts: LinkedAccount[];
  private _isBundleService: boolean = false;
  private _currentPlanDetails: BehaviorSubject<DrupalPlanDetails> = new BehaviorSubject<DrupalPlanDetails>(null);
  private _mobilePlanDetails: BehaviorSubject<MobilePlanDetails> = new BehaviorSubject<MobilePlanDetails>(null);
  private _simType: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private _shouldShowSwitchAccount: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _legacyAccId: string;
  private _hasExistingPaymentPlan: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _hasInflightCollectionsOrder: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _hasOverduePayment: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _hasHomeWirelessService: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _fwServiceIsActive: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _internetPackOptions: BehaviorSubject<CallPack[]> = new BehaviorSubject(null);
  private _agreedToLineSpeedLimitations: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _showMultiserviceFeature: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _isFirstTimeLogin: BehaviorSubject<boolean>  = new BehaviorSubject<boolean>(false);

  public brand$ = this._brand.asObservable();
  public csrMode$ = this._csrMode.asObservable();
  public siteDetails$ = this._siteDetails.asObservable();
  public shouldShowSwitchAccount$ = this._shouldShowSwitchAccount.asObservable();
  public selectedServiceDetails$ = this._selectedServiceDetails.asObservable();
  public fwServiceIsActive$ = this._fwServiceIsActive.asObservable();
  public showMultiserviceFeature$ = this._showMultiserviceFeature.asObservable();
  public linkedAccountBalanceMap$ = this._linkedAccountBalances.asObservable();
  public mobilePlanDetails$ = this._mobilePlanDetails.asObservable();
  public simType$ = this._simType.asObservable();
  public currentPlanDetails$ = this._currentPlanDetails.asObservable();
  public isESIM = false;
  public isFirstTimeLogin$ = this._isFirstTimeLogin.asObservable();

  public disabledForMFA = true;

  public refreshSubject$ = new Subject<any>();
  constants = Constants;

  public static novusPromotionsConfig = {
    genericOffset40: { "promoKey": "OF.Res_NBN_Generic_Offset.v4s1.$40_Off", "promoDiscount": "40" },
    genericOffset20: { "promoKey": "OF.Res_NBN_Generic_Offset.v4s2.$20_Off", "promoDiscount": "20" },
    genericOffset45: { "promoKey": "OF.Res_NBN_Generic_Offset.v4s3.$45_Off", "promoDiscount": "45" },
    tpgClubPricing5Acq: { "promoKey": "OF.Mobile.tpgClubPricing5.Acq", "promoDiscount": "5" },
    tpgClubPricing5Ret: { "promoKey": "OF.Mobile.tpgClubPricing5.Ret", "promoDiscount": "5" },
    tpgClubPricing10Acq: { "promoKey": "OF.Mobile.tpgClubPricing10.Acq", "promoDiscount": "10" },
    tpgClubPricing10Ret: { "promoKey": "OF.Mobile.tpgClubPricing10.Ret", "promoDiscount": "10" },
  };

  public static pcmTechnologyToProductTypeMap = new Map<string, string>([
    ['WIRELESS','NBNWireless'],
    ['SATELLITE','NBNSatellite'],
    ['HFC','NBNFixed'],
    ['FIBRE','NBNFixed'],
    ['FIBRE TO THE CURB','NBNFixed'],
    ['FIBRE TO THE NODE','NBNFixed'],
    ['FIBRE TO THE BUILDING','NBNFixed'],
    ['4G','FixedWireless4G'],
    ['5G','FixedWireless5G'],
    ['VDSL2', 'OnNet'],
    ['ULTRA CABLE', 'OnNet'],
    ['FTTB', 'OnNet']
  ]);


  public static promoCodeErrorMessageMapping = new Map<number, string>([
    [0, "application is unsuccessful. Please try again or call us on [HOTLINE]"],
    [1, "doesn't exist"],
    [2, "cannot be used"],
    [3, "already been claimed. Please try another one"],
    [4, "application is unsuccessful. Please try again or call us on [HOTLINE]"],
    [5, "application is unsuccessful. Please try again or call us on [HOTLINE]"],
  ]);

  constructor(private httpClient: HttpClient) { }

  loadSetUpData(){
    this.user = new User();
    this.serviceDetailsList = [];
    this.amountOwing = 0;
    this._linkedAccounts = [];
  }

  get isBundleService(): boolean {
    return this._isBundleService;
  }

  set isBundleService(value: boolean) {
    this._isBundleService = value;
  }

  get currentPlanDetails(): DrupalPlanDetails {
    return this._currentPlanDetails.getValue();
  }

  set currentPlanDetails(value: DrupalPlanDetails) {
    this._currentPlanDetails.next(value);
  }

  get technology(): string {
    return this._technology.getValue();
  }

  set technology(value: string) {
    this._technology.next(value);
  }

  get user(): User {
    return this._user;
  }

  set user(value: User) {
    this._user = value;
  }

  get mobileInProgressCopDetails(){
    return this._mobileInProgressCopDetails;
  }

  set mobileInProgressCopDetails(mobileInProgressCopDetails: MobileInProgressChangeOfPlanDetails){
    this._mobileInProgressCopDetails = mobileInProgressCopDetails;
  }

  get newFetchOptions(): FetchOptions {
    return this._newFetchOptions;
  }

  set newFetchOptions(value: FetchOptions) {
    this._newFetchOptions = value;
  }



  get serviceDetailsList(): ServiceDetails[] {
    return this._serviceDetailsList;
  }

  get stbOption(): StbOption {
    return this._stbOption;
  }

  set stbOption(value: StbOption) {
    this._stbOption = value;
  }

  set serviceDetailsList(value: ServiceDetails[]) {
    this._serviceDetailsList = value;
  }

  get userCallPacks(): CallPack[] {
    return this._userCallPacks;
  }

  set userCallPacks(value: CallPack[]) {
    this._userCallPacks = value;
  }


  get userFetchOptions(): FetchOptions {
    return this._userFetchOptions;
  }

  set userFetchOptions(value: FetchOptions) {
    this._userFetchOptions = value;
  }

  get selectedServiceDetails(): ServiceDetails {
    return this._selectedServiceDetails.getValue();
  }

  set selectedServiceDetails(value: ServiceDetails) {
    this._selectedServiceDetails.next(value);
  }


  get mobilePlanDetails(): MobilePlanDetails {
    return this._mobilePlanDetails.getValue();
  }

  set mobilePlanDetails(value: MobilePlanDetails) {
    this._mobilePlanDetails.next(value);
  }

  get simType(): string {
      return this._simType.getValue();
    }

  set simType(value: string) {
    if (value?.toUpperCase()=='ESIM') {
      this.isESIM = true;
    }
    this._simType.next(value);
  }

  get billingAccountDetails(): BillingDetails {
    return this._billingAccountDetails;
  }

  set billingAccountDetails(value: BillingDetails) {
    this._billingAccountDetails = value;
  }

  get brand(): string {
    return this._brand.value;
  }

  set brand(value: string) {
    this._brand.next(value);
  }

  get csrMode(): boolean {
    return this._csrMode.value;
  }

  set csrMode(value: boolean) {
    this._csrMode.next(value);
  }

  get amountOwing(): number {
    return this._amountOwing;
  }

  set amountOwing(value: number) {
    this._amountOwing = value;
  }

  get legacyAccId(): string {
      return this._legacyAccId;
   }

  set legacyAccId(value: string) {
    this._legacyAccId = value;
  }

  get bpaInflightMap(): Map<string, boolean> {
    return this._isBpaInflight.value;
  }

  set bpaInflightMap(value: Map<string, boolean>) {
    this._isBpaInflight.next(value);
  }

  get linkedAccountBalanceMap(): Map<string, number> {
    return this._linkedAccountBalances.value;
  }

  set linkedAccountBalanceMap(value: Map<string, number>) {
    this._linkedAccountBalances.next(value);
  }

  get siteDetails(): SiteDetails {
    return this._siteDetails.value;
  }

  set siteDetails(value: SiteDetails) {
    this._siteDetails.next(value);
  }

  get linkedAccounts(): LinkedAccount[] {
    return this._linkedAccounts;
  }

  set linkedAccounts(value: LinkedAccount[]) {
    this._linkedAccounts = value;
  }

  get shouldShowSwitchAccount(): boolean {
    return this._shouldShowSwitchAccount.getValue();
  }

  set shouldShowSwitchAccount(value: boolean) {
    this._shouldShowSwitchAccount.next(value);
  }

  get hasExistingPaymentPlan(): boolean {
    return this._hasExistingPaymentPlan.value;
  }

  set hasExistingPaymentPlan(value: boolean) {
    this._hasExistingPaymentPlan.next(value);
  }

  get hasInflightCollectionsOrder(): boolean {
    return this._hasInflightCollectionsOrder.value;
  }

  set hasInflightCollectionsOrder(value: boolean) {
    this._hasInflightCollectionsOrder.next(value);
  }

  get hasOverduePayment(): boolean {
    return this._hasOverduePayment.value;
  }

  set hasOverduePayment(value: boolean) {
    this._hasOverduePayment.next(value);
  }

  get hasHomeWirelessService(): boolean {
    return this._hasHomeWirelessService.value;
  }

  set hasHomeWirelessService(value: boolean) {
    this._hasHomeWirelessService.next(value);
  }

  get fwServiceIsActive(): boolean {
    return this._fwServiceIsActive.value;
  }

  set fwServiceIsActive(value: boolean) {
    this._fwServiceIsActive.next(value);
  }

  get agreedToLineSpeedLimitations(): boolean {
    return this._agreedToLineSpeedLimitations.value;
  }

  set agreedToLineSpeedLimitations(value: boolean) {
    this._agreedToLineSpeedLimitations.next(value);
  }

  get internetPackOptions(): CallPack[] {
    return this._internetPackOptions.value;
  }

  set internetPackOptions(value: CallPack[]) {
    this._internetPackOptions.next(value);
  }

  get showMultiserviceFeature(): boolean {
    return this._showMultiserviceFeature.getValue();
  }

  set showMultiserviceFeature(value: boolean) {
    this._showMultiserviceFeature.next(value);
  }

  isMultipleCopInProgress(){
    if(this.mobileInProgressCopDetails){
      return this.mobileInProgressCopDetails.latestCopRequestAfterCutoff &&
        this.mobileInProgressCopDetails.latestCopRequestBeforeCutoff;
    }

    return false;
  }

  isSingleCopInProgress(){
    if(this.mobileInProgressCopDetails){
      return !this.isMultipleCopInProgress() &&
        (this.mobileInProgressCopDetails.latestCopRequestAfterCutoff || this.mobileInProgressCopDetails.latestCopRequestBeforeCutoff);
    }

    return false;
  }

  isEmptyMobileCopInProgressDetails(){
    if(this.mobileInProgressCopDetails){
      return (!this.mobileInProgressCopDetails.latestCopRequestAfterCutoff)  && (!this.mobileInProgressCopDetails.latestCopRequestBeforeCutoff)
    }
  }

  isFTTBService() {
    return this.selectedServiceDetails.parentServiceKey.indexOf('FTTB') > -1 && this.selectedServiceDetails.parentServiceKey.indexOf('NBN') === -1;
  }

  checkShouldShowSwitchAccount(): void {
    for (const linkedAccount of this.linkedAccounts) {
      if (linkedAccount.sourceSystem === 'SNBS' || linkedAccount.sourceSystem === 'RUMBA' || linkedAccount.sourceSystem === 'IAS') {
          this.shouldShowSwitchAccount = true;
          this.legacyAccId = linkedAccount.account;
          return;
      }
    }
    this.shouldShowSwitchAccount = false;
  }

  shouldShowMultiServiceFeature(): boolean {
    const ineligibleServiceExists = this.serviceDetailsList.some(elem => this.checkIneligibleMultiserviceProduct(elem));
    return !ineligibleServiceExists;
  }

  checkIneligibleMultiserviceProduct(serviceDetails: ServiceDetails): boolean {
    if (!serviceDetails.parentServiceKey || (!serviceDetails.serviceIsBroadband && !serviceDetails.serviceIsMobile)) {
      return false;
    }
    if (serviceDetails.category === 'BUSINESS_NBN') {
      return true;
    }
    return false;
  }

  checkIsSatellite() {
    if (this.selectedServiceDetails != null && this.selectedServiceDetails.parentServiceKey !== null) {
      return this.selectedServiceDetails.parentServiceKey.toLowerCase().indexOf('satellite') !== -1;
    }
  }

  checkIs5GFw() {
    return this.selectedServiceDetails !== null
      && this.selectedServiceDetails.serviceKey !== null
      && this.selectedServiceDetails.category === 'HOME_WIRELESS'
      && this.selectedServiceDetails.serviceKey.indexOf('_5G_');
  }

  isUltraCable() {
    if (this.selectedServiceDetails != null && this.selectedServiceDetails.parentServiceKey !== null) {
      return (this.selectedServiceDetails.parentServiceKey.toLowerCase().indexOf('ultracable') !== -1);
    }
    return false;
  }

  isTurboFibre() {
    if (this.selectedServiceDetails != null && this.selectedServiceDetails.parentServiceKey !== null) {
      return (this.selectedServiceDetails.parentServiceKey.toLowerCase().indexOf('turbofibre') !== -1);
    }
    return false;
  }

  isVirtualPhoneService(){
    if (this.selectedServiceDetails != null && this.selectedServiceDetails.serviceName !== null) {
      return (this.selectedServiceDetails.serviceName.indexOf('Virtual Phone') !== -1);
    }
    return false;
  }

  hasVirtualPhoneService() {
    return this.serviceDetailsList.some(serviceDetails => (
      (serviceDetails.serviceName.indexOf('Virtual Phone') !== -1)
      && (this.selectedServiceDetails.billingAccountId === serviceDetails.billingAccountId)
    ));
  }

  getBroadbandServices() {
    return this.serviceDetailsList.filter(serviceDetails => {
      return serviceDetails.serviceIsBroadband;
    });
  }

  getMobileServices() {
    return this.serviceDetailsList.filter(serviceDetails => {
      return serviceDetails.serviceIsMobile;
    });
  }

  getServicesByCategory(category: string) {
    return this.serviceDetailsList.filter(serviceDetails =>
      serviceDetails.category.toLowerCase() === category.toLowerCase());
  }

  isSelectedServiceOfCategory(category: string) {
    return this.selectedServiceDetails.category.toLowerCase() === category.toLowerCase();
  }

  getFeatureValueFromName(serviceDetail: ServiceDetails, name: string): string {
    if (serviceDetail && serviceDetail.features && serviceDetail.features.features) {
      for (let feature of serviceDetail.features.features) {
        if (name === feature.name) {
          if (feature.value) {
            return feature.value.value;
          }
        }
      }
    }
    return null;
  }

  getFeatureValueFromKey(service: ServiceDetails, key: string): string {
    if (service && service.features && service.features.features) {
      const features: Features[] = service.features.features;
      for (const i in features) {
        if (features[i].key.toLowerCase().includes(key.toLowerCase())) {
          return features[i].value.value;
        }
      }
    }
    return null;
  }

  isHomeWireless() {
    return this.selectedServiceDetails !== null
      && this.selectedServiceDetails.serviceKey !== null
      && this.selectedServiceDetails.category === 'HOME_WIRELESS';
  }

  setTechnology(selectedService: ServiceDetails) {
    if (selectedService?.features?.features) {
      for (const feature of selectedService.features.features) {
        if (feature.name?.toLowerCase() === "technology") {
          this.technology = feature.value.value;
        }
      }
    }
  }

  get isFirstTimeLogin(): boolean {
    return this._isFirstTimeLogin.value;
  }

  set isFirstTimeLogin(value: boolean) {
    this._isFirstTimeLogin.next(value);
  }

  isActiveMobileService(): boolean {
    return this.selectedServiceDetails?.serviceIsMobile &&
    (this.selectedServiceDetails?.serviceActivationDate != null);
  }

  isBrandSupportsEsim(): boolean {
    return (this.constants.BRAND_IINET === this.brand || this.constants.BRAND_TPG === this.brand);
  }

  isBrandSupportsRationalisation(): boolean {
    return (this.constants.BRAND_INTERNODE === this.brand  || this.constants.BRAND_IINET === this.brand);
  }

  isPremiumCallPack (callPackOption: CallPack): boolean {
    return callPackOption.key.toLowerCase().includes('premium');
  }

  isValueCallPack (callPackOption: CallPack): boolean {
    return callPackOption.key.toLowerCase().includes('value');
  }

  isBasicCallPack (callPackOption: CallPack): boolean {
    return callPackOption.key.toLowerCase().includes('basic');
  }

  isUnlimitedCallPackOption (callPackOption: CallPack): boolean {
    return callPackOption.callPackInternationalMinutes !== null && callPackOption.callPackInternationalMinutes.toLowerCase().includes('unlimited');
  }

  isBasicOrValueOrPremiumCallPackOption (callPackOption: CallPack): boolean {
    return this.isBasicCallPack(callPackOption) || this.isValueCallPack(callPackOption) || this.isPremiumCallPack(callPackOption);
  }

  isSeniorsPlan() {
    return this.selectedServiceDetails?.serviceKey?.toLowerCase().includes("senior") || this.selectedServiceDetails?.parentServiceKey?.toLowerCase().includes("senior");
  }

}
